import React from 'react';
import { Table } from 'react-bootstrap';
import { SportProvider } from '../../models/interfaces/SportProvider';
import { MatchSegmentStats } from '../../models/interfaces/Stat';
import { Match, MatchStatus } from '../../models/interfaces/Match';
import { MatchEntry } from '../../models/interfaces/MatchEntry';
import { parseTeamColor } from '../../helpers/parseTeamColor';
import styled from 'styled-components';

const TeamTR = styled.tr`
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    box-shadow: inset 0.3rem 0 0 0 ${({ color }) => color};
`;

interface Props {
    match: Match;
    matchStatsList: MatchSegmentStats[] | undefined;
    sportProvider: SportProvider;
    lightTeamScore?: number;
    darkTeamScore?: number;
}

interface RowData {
    team: MatchEntry;
    matchSegmentScoreText: string[];
    total: string;
}

export default function MatchBoxScoreView({
    match,
    matchStatsList,
    sportProvider,
    lightTeamScore,
    darkTeamScore,
}: Props) {
    const defaultNumberOfMatchSegments =
        match.parameters.regularTimeDescription.matchSegments.length;
    let largestSegmentDBValue = 0;
    if (matchStatsList) {
        largestSegmentDBValue = matchStatsList.reduce(
            (max, next) =>
                max > next.matchSegment.databaseValue ? max : next.matchSegment.databaseValue,
            0
        );
    }
    const numberOfMatchSegments = Math.max(largestSegmentDBValue + 1, defaultNumberOfMatchSegments);
    const matchSegments = Array.from({ length: numberOfMatchSegments }).map((_, index) => {
        const matchSegment = sportProvider.matchSegment(
            match.parameters.regularTimeMatchSegmentOption,
            index
        );
        return matchSegment;
    });
    const rows: RowData[] = [match.lightCapTeam, match.darkCapTeam].map((team) => {
        let total = 0;
        const matchSegmentScoreText = matchSegments.map((matchSegment) => {
            const matchSegmentStats = matchStatsList
                ? matchStatsList.find((matchStats) => {
                      return matchStats.matchSegment.databaseValue === matchSegment.databaseValue;
                  })
                : undefined;

            if (matchSegmentStats) {
                return String(
                    matchSegmentStats.stats.reduce((scoreForMatchSegment, stat) => {
                        if (stat.team && stat.team.id === team.id) {
                            const scoreValue = stat.scoreValue;
                            total += scoreValue;
                            return scoreForMatchSegment + scoreValue;
                        } else {
                            return scoreForMatchSegment;
                        }
                    }, 0)
                );
            } else {
                if (
                    matchSegment.databaseValue < largestSegmentDBValue ||
                    match.status === MatchStatus.completed
                ) {
                    return '0';
                } else {
                    return '-';
                }
            }
        });
        // hack to display score if only final score was provided
        if (
            total === 0 &&
            ((lightTeamScore && lightTeamScore !== 0) || (darkTeamScore && darkTeamScore !== 0))
        ) {
            if (team.id === match.lightCapTeam.id) {
                total = lightTeamScore || 0;
            } else {
                total = darkTeamScore || 0;
            }
        }
        return {
            team: team,
            matchSegmentScoreText: matchSegmentScoreText,
            total: String(total),
        };
    });

    return (
        <Table striped bordered>
            <thead>
                <tr>
                    <th></th>
                    {matchSegments.map((matchSegment) => (
                        <th key={matchSegment.displayName}>{matchSegment.displayName}</th>
                    ))}
                    <th>Total</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((rowData) => {
                    return (
                        <TeamTR key={rowData.team.name} color={parseTeamColor(rowData.team.color)}>
                            <td>{rowData.team.name}</td>
                            {rowData.matchSegmentScoreText.map((scoreText, i) => (
                                <td key={`${rowData.team.name}-${i}-${scoreText}`}>{scoreText}</td>
                            ))}
                            <td>{rowData.total}</td>
                        </TeamTR>
                    );
                })}
            </tbody>
        </Table>
    );
}
